import { CircularProgress, Grid, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useRef } from 'react';
import { TKeyValue } from '../../../../../utils/commonInterfaces';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import {
  useStudentFiltersState,
  useStudentFiltersActions,
} from '../../contexts/studentFilters.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useGradeAndFeeStructureHook } from '../../../institute/hooks/useGradeAndFeeStructureHook';
import {
  GRADE_OPTIONS,
  SECTION_OPTIONS,
} from '../../constants/filters.constants';
import {
  IClassFeeSection,
  IClassStructureSchema,
} from '../../../../../apis/institute/institute.interfaces';
import isAppWithMock from 'src/utils/isAppWithMock';

export type StudentListFiltersProps = {
  onFilterChange: (name: string, value: string) => void;
  isPrimaryFilters?: boolean;
};

const filterValidSections = (sections: IClassFeeSection[]) =>
  sections.filter(
    (s) => Boolean(s.sectionId?.trim()) && Boolean(s.label?.trim())
  );

const StudentListFilters: FC<StudentListFiltersProps> = ({
  onFilterChange,
  isPrimaryFilters = false,
}) => {
  const withMock = isAppWithMock();
  const filters = useStudentFiltersState();
  const { updateFilters } = useStudentFiltersActions();
  const { translateText: t } = useTranslation();

  const { data: classFeesData, isLoading: classFeesLoading } =
    useGradeAndFeeStructureHook({
      enabled: !withMock,
    });

  const classesFromApi = useMemo(
    () => classFeesData?.classes ?? [],
    [classFeesData]
  );

  const gradeList: TKeyValue<string>[] = useMemo(() => {
    if (withMock) {
      return GRADE_OPTIONS;
    }
    return classesFromApi.map((item: IClassStructureSchema) => ({
      key: item.classId,
      value: item.className,
    }));
  }, [withMock, classesFromApi]);

  const selectedClass = useMemo(() => {
    if (withMock || filters.grade === 'select') {
      return undefined;
    }
    return classesFromApi.find((c) => c.classId === filters.grade);
  }, [withMock, filters.grade, classesFromApi]);

  const sectionsForSelectedClass: IClassFeeSection[] = useMemo(() => {
    if (withMock) {
      return [];
    }
    return filterValidSections(selectedClass?.sections ?? []);
  }, [withMock, selectedClass]);

  const sectionList: TKeyValue<string>[] = useMemo(() => {
    if (withMock) {
      return SECTION_OPTIONS;
    }
    return sectionsForSelectedClass.map((s) => ({
      key: s.sectionId,
      value: s.label,
    }));
  }, [withMock, sectionsForSelectedClass]);

  const showSectionControl =
    withMock ||
    (filters.grade !== 'select' && sectionsForSelectedClass.length > 1);

  const prevGradeForSectionResetRef = useRef(filters.grade);

  useEffect(() => {
    if (withMock) {
      return;
    }
    const prev = prevGradeForSectionResetRef.current;
    const curr = filters.grade;
    if (prev !== curr) {
      prevGradeForSectionResetRef.current = curr;
      if (curr !== 'select') {
        const cls = classesFromApi.find((c) => c.classId === curr);
        const secs = filterValidSections(cls?.sections ?? []);
        if (secs.length > 1) {
          updateFilters({ section: 'select' });
        }
      }
    }
  }, [withMock, filters.grade, classesFromApi, updateFilters]);

  useEffect(() => {
    if (withMock || filters.grade === 'select' || !classFeesData) {
      return;
    }
    const cls = classesFromApi.find((c) => c.classId === filters.grade);
    const secs = filterValidSections(cls?.sections ?? []);
    if (secs.length === 1) {
      const id = secs[0].sectionId;
      if (filters.section !== id) {
        updateFilters({ section: id });
      }
    } else if (secs.length === 0) {
      if (filters.section !== 'select') {
        updateFilters({ section: 'select' });
      }
    }
  }, [
    withMock,
    filters.grade,
    filters.section,
    classFeesData,
    classesFromApi,
    updateFilters,
  ]);

  const enhancedGradeList: TKeyValue<string>[] = [
    { key: 'select', value: t('students.filters.select') },
    ...(gradeList?.filter((item) => item && item.key && item.value) ?? []),
  ];

  const enhancedSectionList: TKeyValue<string>[] = [
    { key: 'select', value: t('students.filters.select') },
    ...(sectionList?.filter((item) => item && item.key && item.value) ?? []),
  ];

  const handleFilterChange = (name: string, value: string) => {
    updateFilters({ [name]: value });
    onFilterChange(name, value);
  };

  return (
    <Grid
      container
      gap={{ xs: 1, sm: 2 }}
      data-testid="student-list-filters"
      key={isPrimaryFilters ? 'primary-filters' : 'secondary-filters'}
      sx={{
        alignItems: 'center',
        flex: 1,
        justifyContent: 'flex-end',
        flexDirection: 'row',
        ...(isPrimaryFilters ? {
          mt: '25vh',
        } : {}),
        '& .MuiFormControl-root': {
          minWidth: { xs: '100%', sm: 200 },
          width: { xs: '100%', sm: 'auto' },
          '& .MuiInputLabel-root': {
            backgroundColor: 'background.paper',
            px: 0.5,
          },
        },
      }}
    >
      {isPrimaryFilters ? (
        <>
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                mb: { xs: 1, sm: 0 },
              }}
            >
              {t('students.filters.primaryFilters')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                mb: { xs: 2, sm: 0 },
              }}
            >
              {t('students.filters.selectClassMessage')}
            </Typography>
          </Grid>
        </>
      ) : (
        <Grid item xs={12} sm="auto">
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '0.875rem', sm: 'inherit' },
              mb: { xs: 1, sm: 0 },
            }}
          >
            {t('students.filters.filters')}:
          </Typography>
        </Grid>
      )}
      <Grid
        item
        xs={12}
        sm={!isPrimaryFilters ? "auto" : 12}
        sx={{
          minWidth: { xs: '100%', sm: 160 },
          width: { xs: '100%', sm: 'auto' },
          display: 'flex',
          ...(isPrimaryFilters ? {
            mt: 4,
          } : {}),
          justifyContent: 'center',
          '& .MuiInputLabel-root': {
            transform: 'translate(14px, -9px) scale(0.75)',
            background: '#fff',
            padding: '0 8px',
          },
          '& .MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        }}
      >
        {!withMock && classFeesLoading ? (
          <CircularProgress size={28} aria-label={t('common.loading')} />
        ) : (
          <ControlledSelectField
            label={t('students.filters.class')}
            name="grade"
            itemList={enhancedGradeList}
            id="grade"
            value={filters.grade}
            dataTestId="student-list-class-select-field"
            isFullWidth={false}
            isGridItem={false}
            onChangeHandler={(e) => handleFilterChange('grade', e.target.value)}
          />
        )}
      </Grid>
      {showSectionControl ? (
        <Grid
          item
          xs={12}
          sm={!isPrimaryFilters ? 'auto' : 12}
          sx={{
            minWidth: { xs: '100%', sm: 160 },
            width: { xs: '100%', sm: 'auto' },
            display: 'flex',
            justifyContent: 'center',
            ...(isPrimaryFilters
              ? {
                  mt: 4,
                }
              : {}),
            '& .MuiInputLabel-root': {
              transform: 'translate(14px, -9px) scale(0.75)',
              background: '#fff',
              padding: '0 8px',
            },
            '& .MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',
            },
          }}
        >
          <ControlledSelectField
            label={t('students.filters.section')}
            name="section"
            itemList={enhancedSectionList}
            id="section"
            isFullWidth={false}
            isGridItem={false}
            dataTestId="student-list-section-select-field"
            value={filters.section}
            onChangeHandler={(e) =>
              handleFilterChange('section', e.target.value)
            }
            key={filters.section}
          />
        </Grid>
      ) : null}
    </Grid>
  );
};

export default StudentListFilters;
